import {
    get,
    first,
    forEach,
    indexOf,
    keys,
    map,
    noop,
    range,
    slice,
    sortBy,
    values,
    zip,
} from 'lodash';

import moment from 'moment';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { withTheme } from 'styled-components';

import { DATE_FORMATS, SIZE_TO_NUM_COLS } from '../constants/ViewConstants';
import * as GridEvents from '../constants/TimesGridStateStoreEvents';
import * as AppointmentEvents from '../constants/AppointmentTimesStoreEvents';
import TimesGridActions from '../actions/TimesGridActions';
import TimesGridStateStore from '../stores/TimesGridStateStore';
import { IntlProvider } from 'react-intl';
import { appendQueryParams } from '../../utils/append-query-params';

export default function CreateTimesGridContent(config) {
    return function wrapComponent(WrappedComponent) {
        class BaseTimesGridContent extends PureComponent {
            static propTypes = {
                // Required Parameters
                gridActions: PropTypes.instanceOf(TimesGridActions).isRequired,
                gridStateStore: PropTypes.instanceOf(TimesGridStateStore).isRequired,

                // Optional Parameters
                numRowsOnLoad: PropTypes.number, // > 0
                shouldShowAllRows: PropTypes.bool,
                onClickNextAvailable: PropTypes.func,
                onClickPhoneNumber: PropTypes.func,
                onLoad: PropTypes.func,
                phoneNumber: PropTypes.string,
                profileUrl: PropTypes.string,
                theme: PropTypes.object,
                additionalBookingUrlParams: PropTypes.object,
            };

            static defaultProps = {
                numRowsOnLoad: 5,
                shouldShowAllRows: false,
                onClickNextAvailable: noop,
                onClickPhoneNumber: noop,
                onLoad: noop,
                additionalBookingUrlParams: {},
            };

            constructor(...args) {
                super(...args);

                const shouldShowAllRows = this.props.shouldShowAllRows || config.SHOULD_SHOW_ALL_ROWS;
                this.state = this.getTimesGridContentState(shouldShowAllRows);

                /**
                 * If the API resolves before this component is constructed, an externally-
                 * provided loading state will not be dismissed properly.
                 */
                if (this.props.gridStateStore.getState().initialized) {
                    this.state.loading = false;
                }
            }

            componentDidMount() {
                const { doctorLocationId, gridStateStore, onLoad } = this.props;
                // Any events added here should be unbound in componentWillUnmount
                gridStateStore.on(GridEvents.CHANGE_AVAILABILITY_TIME_FILTER, this.handleChange);
                gridStateStore.on(GridEvents.CHANGE_DATE, this.handleChange);
                gridStateStore.on(GridEvents.CHANGE_DOCTOR_LOCATION_IDS, this.handleChangeIds);
                gridStateStore.on(GridEvents.CHANGE_IS_EXISTING_PATIENT, this.handleIsExistingPatientChange);
                gridStateStore.on(GridEvents.CHANGE_PROCEDURE, this.handleChange);
                gridStateStore.on(GridEvents.CHANGE_SELECTED_TIMESLOT, this.handleChange);

                this.bindTimesStoreEventHandlers();
                onLoad({
                    doctorLocationId,
                    ...gridStateStore.getState().toJSON(),
                });
            }

            componentDidUpdate(prevProps) {
                if (prevProps.isExistingPatient !== this.props.isExistingPatient) {
                    this.handleIsExistingPatientChange();
                }
            }

            componentWillUnmount() {
                const { gridStateStore } = this.props;
                gridStateStore.off(GridEvents.CHANGE_AVAILABILITY_TIME_FILTER, this.handleChange);
                gridStateStore.off(GridEvents.CHANGE_DATE, this.handleChange);
                gridStateStore.off(GridEvents.CHANGE_DOCTOR_LOCATION_IDS, this.handleChangeIds);
                gridStateStore.off(GridEvents.CHANGE_IS_EXISTING_PATIENT, this.handleIsExistingPatientChange);
                gridStateStore.off(GridEvents.CHANGE_PROCEDURE, this.handleChange);
                gridStateStore.off(GridEvents.CHANGE_SELECTED_TIMESLOT, this.handleChange);

                this.unbindTimesStoreEventHandlers();
            }

            render() {
                const { dateFormat } = this.props.theme.components.AppointmentPicker;
                const intlConfig = { defaultFormats: { date: { dateFormat } } };

                return (
                    <IntlProvider {...intlConfig}>
                        <WrappedComponent
                            {...this.getEntireTimesGridMessageViewModel(
                                this.state.times,
                                this.props.gridStateStore.getState().initialized,
                                this.state.loading,
                                this.props.gridStateStore,
                            )}
                            goToDayWithAvailBtnClicked={this.goToDayWithAvailBtnClicked}
                            onTimeClicked={this.onTimeClicked}
                            onTimeMouseUp={this.onTimeMouseUp}
                            {...this.props}
                            {...this.state}
                            times={this.getGridRowsViewModel(this.state.times)}
                        />
                    </IntlProvider>
                );
            }

            /**
             * Gets the view model required to render the grid rows on the timesgrid
             *
             * @param times
             * @returns {Array.<object>}
             * @private
             */
            getGridRowsViewModel(times) {
                const timeslotRowsViewModel = [];

                // build matrix for times grid

                // - sort to make sure the times are in date order
                const dateStrKeys = keys(times);
                const sortedDateStrKeys = this.getSortedDateStrings(dateStrKeys);
                const interestedDateStrKeys = slice(sortedDateStrKeys, 0, this.getNumberDaysToDisplay());
                const sortedByDayAppts = map(interestedDateStrKeys, dateStr => {
                    return sortBy(times[dateStr].appointments, appt => moment(appt.date).unix());
                });

                // - each row in the matrix is a row in the times grid
                if (sortedByDayAppts.length > 0) {
                    const transposedTimes = zip(...sortedByDayAppts);
                    forEach(transposedTimes, (timesGridRow, rowIndex) => {
                        const timesGridRowTransformed = [];

                        forEach(timesGridRow, (timeslotModel, colIndex) => {
                            const datetimeStr = timeslotModel ? moment(timeslotModel.date).format('h:mm a') : null;

                            timeslotModel = timeslotModel || {};

                            let timeslotViewModel = this.getTimeslotViewModel(
                                colIndex,
                                timeslotModel.id,
                                datetimeStr,
                                timeslotModel.url
                            );

                            if (!this.state.shouldShowAllRows
                                && rowIndex === this.props.numRowsOnLoad - 1
                                && !!transposedTimes[rowIndex + 1]
                                && !!transposedTimes[rowIndex + 1][colIndex]) {
                                // make a "more" button
                                timeslotViewModel = this.getTimeslotViewModel(
                                    colIndex,
                                    null,
                                    'more',
                                    'javascript:;', // eslint-disable-line
                                    true
                                );
                            }

                            timesGridRowTransformed.push(timeslotViewModel);
                        }, this);

                        if (!this.state.shouldShowAllRows && rowIndex > this.props.numRowsOnLoad - 1) {
                            // shouldn't display anymore timeslots

                        } else {
                            timeslotRowsViewModel.push(this.getTimesRowViewModel(timesGridRowTransformed, rowIndex));
                        }
                    }, this);
                }

                if (timeslotRowsViewModel.length < this.props.numRowsOnLoad) {
                    // add more rows so its at least length of this.viewConfig.numRowsOnLoad
                    const currNumRows = timeslotRowsViewModel.length;
                    const numRowsToAdd = this.props.numRowsOnLoad - currNumRows;

                    range(numRowsToAdd).map(index => (
                        timeslotRowsViewModel.push(this.getEmptyTimesRowViewModel(index + currNumRows))
                    ));
                }

                return timeslotRowsViewModel;
            }

            /**
             * Sorts the given date strings in ascending order
             *
             * @param dateStrings
             * @returns {{criteria, index, value}|Array}
             * @private
             */
            getSortedDateStrings(dateStrings) {
                // returns unix offset in seconds
                return sortBy(dateStrings, dateStr => moment(dateStr).unix());
            }

            getTimeslotViewModel(colIndex, id, datetimeStr, absoluteUrl, isMoreBtn) {
                let timeslotUrl = absoluteUrl;

                if (!isMoreBtn && !!absoluteUrl) {
                    const additionalParams = this.props.additionalBookingUrlParams;
                    timeslotUrl = appendQueryParams(absoluteUrl, additionalParams);
                }

                return {
                    colIndex,
                    id,
                    datetime: datetimeStr,
                    url: timeslotUrl,
                    isMoreBtn,
                };
            }

            getTimesRowViewModel(timeslotViewModelsInRow, rowIndex) {
                let firstNoTimesDayForWeek = 999;

                if (rowIndex >= this.props.numRowsOnLoad) {
                    forEach(timeslotViewModelsInRow, (timeslotViewModel, index) => {
                        if (!!timeslotViewModel.id && firstNoTimesDayForWeek > index) {
                            firstNoTimesDayForWeek = index;
                        }
                    });
                } else {
                    // need to display at least this.props.numRowsOnLoad rows
                    firstNoTimesDayForWeek = 0;
                }

                return {
                    times: timeslotViewModelsInRow,
                    firstNoTimesDayForWeek,
                };
            }

            getEmptyTimesRowViewModel(rowIndex) {
                const emptyTimeslotViewModels = [];

                forEach(range(config.MAX_VISIBLE_DAYS), colIndex => {
                    emptyTimeslotViewModels.push(this.getTimeslotViewModel(colIndex));
                }, this);

                return this.getTimesRowViewModel(emptyTimeslotViewModels, rowIndex);
            }

            /**
             * Gets the view model required to display messages that
             * cover the entire timesgrid area.
             *
             * @param {Array.<object>} times
             * @returns {object}
             * @private
             */
            getEntireTimesGridMessageViewModel(times, initialized, loading = false) {
                let firstDateWithTimeslots = null;
                let firstDateWithTimeSlotsIndex = null;
                let showEntireTimesGridMessage = false;
                let showExistingPatientLink = false;
                let nextAvailDate = null;

                const numberOfDaysVisible = this.getNumberDaysToDisplay();
                const firstDateNextAppt = first(values(times)); // first date supplied
                const showGovernmentInsuranceNotice = get(firstDateNextAppt, 'showGovernmentInsuranceNotice');
                const timesKeys = keys(times);
                const timesToBeDisplayed = timesKeys.slice(0, numberOfDaysVisible);

                if (firstDateNextAppt && firstDateNextAppt.appointments.length === 0) {
                    if (firstDateNextAppt.nextAppointment) {
                        firstDateWithTimeslots = moment(firstDateNextAppt.nextAppointment._d);

                        const firstDateWithTimeslotsString = firstDateWithTimeslots.format(DATE_FORMATS.DISPLAY);

                        firstDateWithTimeSlotsIndex = indexOf(timesToBeDisplayed, firstDateWithTimeslotsString);

                        if (firstDateWithTimeSlotsIndex === -1) {
                            showEntireTimesGridMessage = true;
                            nextAvailDate = firstDateWithTimeslots;
                        }
                    } else {
                        // there is no next available date
                        showEntireTimesGridMessage = true;
                    }
                }

                if (!nextAvailDate && !firstDateNextAppt && !loading && initialized) {
                    showEntireTimesGridMessage = true;
                }

                // Check whether to show existing patient message
                if (!this.state.isExistingPatient && showEntireTimesGridMessage) {
                    const { doctorLocationId, gridStateStore } = this.props;
                    const existingPatientTimeslots = gridStateStore.existingPatientTimesStore.getByDoctorLocation(
                        doctorLocationId,
                        gridStateStore.getState().date, // current date
                        config.MAX_VISIBLE_DAYS
                    );

                    if (existingPatientTimeslots.hasLoaded) {
                        const firstExistingPatientDate = first(values(existingPatientTimeslots.times));

                        showExistingPatientLink = Boolean(
                            firstExistingPatientDate &&
                            firstExistingPatientDate.nextAppointment
                        );
                    }
                }

                return {
                    showExistingPatientLink,
                    showEntireTimesGridMessage,
                    showNextTimeAvailable: firstDateWithTimeSlotsIndex !== null,
                    showGovernmentInsuranceNotice,
                    nextAvailDate,
                };
            }

            handleIsExistingPatientChange = e => {
                this.unbindTimesStoreEventHandlers();
                this.bindTimesStoreEventHandlers();
                this.handleChange(e);
            };

            handleChange = shouldShowAllRows => {
                this.setState(this.getTimesGridContentState(shouldShowAllRows));
            };

            onTimeClicked = (isMoreBtn, timeUrl, e) => {
                e.stopPropagation();

                if (isMoreBtn) {
                    e.preventDefault(); // prevent the href from being triggered on re-render
                    this.handleChange(true);
                    this.props.gridActions.clickMoreTimes();
                    if (this.props.onClickMore) this.props.onClickMore();
                } else {
                    const timeslot = this.props.gridStateStore.timesStore.getByTimeslot(e.currentTarget.getAttribute('data-id'));

                    if (this.props.onTimeSlotClicked) {
                        this.props.onTimeSlotClicked(timeslot, e);
                    }

                    if (timeslot) {
                        this.props.gridActions.showConstraint(e, timeslot);
                    }
                }
            };

            onTimeMouseUp = (isMoreBtn, timeUrl, e) => {
                e.stopPropagation();

                // left click and middle click are treated as clicks, so we don't handle those here
                // just handle the right click
                if (e.button === 2) {
                    this.onTimeClicked(isMoreBtn, timeUrl, e);
                }
            };

            goToDayWithAvailBtnClicked = (numDaysVisible, nextAvailDate, e) => {
                e.stopPropagation();

                this.props.gridActions.changeNumDaysVisible(numDaysVisible);
                this.props.gridActions.jumpToDate(nextAvailDate, this.props.guid, e.button);

                this.props.onClickNextAvailable(e);
            };

            bindTimesStoreEventHandlers = () => {
                const { gridStateStore } = this.props;
                gridStateStore.timesStore.on(AppointmentEvents.ALL_TIMES_RECEIVED, this.toggleOffLoadingState);
                gridStateStore.timesStore.on(AppointmentEvents.ERROR, this.toggleOffLoadingState);
                gridStateStore.timesStore.on(AppointmentEvents.LOAD_TIMES, this.handleChange);
                gridStateStore.timesStore.on(AppointmentEvents.RESET, this.handleChange);
            };

            unbindTimesStoreEventHandlers = () => {
                const { gridStateStore: { newPatientTimesStore, existingPatientTimesStore } } = this.props;
                // We don't know which times store (existing or new) was active, so remove listeners on both stores.
                [newPatientTimesStore, existingPatientTimesStore].forEach(timesStore => {
                    timesStore.off(AppointmentEvents.ALL_TIMES_RECEIVED, this.toggleOffLoadingState);
                    timesStore.off(AppointmentEvents.ERROR, this.toggleOffLoadingState);
                    timesStore.off(AppointmentEvents.LOAD_TIMES, this.handleChange);
                    timesStore.off(AppointmentEvents.RESET, this.handleChange);
                });
            };

            handleChangeIds = shouldShowAllRows => this.setState(
                this.getTimesGridContentState(
                    shouldShowAllRows,
                    this.props.gridStateStore.getState().doctorLocationIds[0]
                )
            );

            getTimesGridContentState(shouldShowAllRows, doctorLocationId = this.props.doctorLocationId) {
                const times = this.props.gridStateStore.timesStore.getByDoctorLocation(
                    doctorLocationId,
                    this.props.gridStateStore.getState().date, // current date
                    config.MAX_VISIBLE_DAYS
                );

                return {
                    loading: !times.hasLoaded,
                    times: times.hasLoaded ? times.times : {},
                    shouldShowAllRows: (this.state && this.state.shouldShowAllRows) || shouldShowAllRows,
                };
            }

            getNumberDaysToDisplay() {
                return SIZE_TO_NUM_COLS[this.props.sizeId] || 1;
            }

            toggleOffLoadingState = () => this.setState({ loading: false });
        }

        return withTheme(BaseTimesGridContent);
    };
}
